import React from 'react';
import { Link } from 'react-router-dom';
import LegalLinks from '../components/LegalLinks';
import { useSeo } from '../hooks/useSeo';
import { seo, LOGO_URL } from '../seoConfig';
import { privacy, EMAIL } from '../privacyContent';

const NAV = {
  tr: [['/tr/izmir-chip-tuning', 'Ana Sayfa'], ['/calculator', 'Güç Hesaplayıcı'], ['/faq', 'SSS']],
  en: [['/', 'Home'], ['/calculator', 'Power Calculator'], ['/faq', 'FAQ']],
};

const withEmail = (text) =>
  String(text).split(EMAIL).flatMap((part, i) => (i === 0
    ? [part]
    : [<a key={i} href={`mailto:${EMAIL}`} className="text-[#FF6600] hover:underline">{EMAIL}</a>, part]));

const Block = ({ block }) => {
  if (block.p) return <p className="text-gray-300 leading-relaxed mb-4">{withEmail(block.p)}</p>;
  if (block.lines) {
    return (
      <p className="text-gray-300 leading-relaxed mb-4">
        {block.lines.map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{withEmail(line)}</React.Fragment>)}
      </p>
    );
  }
  if (block.ul) {
    return (
      <ul className="list-disc pl-6 space-y-2 text-gray-300 leading-relaxed mb-4 marker:text-[#FF6600]">
        {block.ul.map((item, i) => (Array.isArray(item)
          ? <li key={i}><strong className="text-white font-semibold">{item[0]}:</strong> {withEmail(item[1])}</li>
          : <li key={i}>{withEmail(item)}</li>))}
      </ul>
    );
  }
  if (block.table) {
    return (
      <div className="overflow-x-auto mb-4">
        <table className="w-full text-left text-gray-300 border border-gray-800 text-sm md:text-base">
          <thead className="bg-[#1a1a1a] text-white">
            <tr>{block.table.head.map((h) => <th key={h} className="px-4 py-3 border-b border-gray-800 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {block.table.rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-800 last:border-b-0 align-top">
                {row.map((cell, j) => <td key={j} className="px-4 py-3">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

const PrivacyPolicy = ({ lang = 'en' }) => {
  const doc = privacy[lang] || privacy.en;
  useSeo(lang === 'tr' ? seo.privacyTr : seo.privacyEn);

  return (
    <div className="min-h-screen bg-black">
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={NAV[doc.lang][0][0]} className="flex items-center">
              <img src={LOGO_URL} alt="Powerlab Works" className="h-14 w-auto object-contain" />
            </Link>
            <nav className="flex items-center space-x-6 md:space-x-8">
              {NAV[doc.lang].map(([to, label]) => (
                <Link key={to} to={to} className="hidden md:inline text-gray-300 hover:text-[#FF6600] transition-colors">{label}</Link>
              ))}
              <Link to={doc.altPath} hrefLang={doc.lang === 'tr' ? 'en' : 'tr'} className="text-gray-300 hover:text-[#FF6600] transition-colors">
                {doc.lang === 'tr' ? 'EN' : 'TR'}
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-16 bg-gradient-to-b from-black to-[#0a0a0a]">
        <article className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">{doc.title}</h1>
          <p className="text-gray-500 text-sm mb-8">{doc.updated}</p>
          {doc.intro.map((text, i) => <p key={i} className="text-gray-300 leading-relaxed mb-4">{text}</p>)}
          {doc.sections.map((section) => (
            <section key={section.h}>
              <h2 className="text-2xl font-semibold text-white mt-10 mb-4">{section.h}</h2>
              {section.blocks.map((block, i) => <Block key={i} block={block} />)}
            </section>
          ))}
        </article>
      </main>

      <footer className="bg-black border-t border-gray-900 py-10">
        <div className="container mx-auto px-4 text-center">
          <img src={LOGO_URL} alt="Powerlab" className="h-12 w-auto object-contain mx-auto mb-4" />
          <p className="text-gray-600 text-xs">
            {doc.lang === 'tr' ? '© 2025 Powerlab Works OU. Tüm hakları saklıdır.' : '© 2025 Powerlab Works OU. All rights reserved.'}
          </p>
          <LegalLinks lang={doc.lang} className="mt-3" />
          <p className="mt-3">
            <Link to={doc.altPath} className="text-[#FF6600] hover:underline text-sm" hrefLang={doc.lang === 'tr' ? 'en' : 'tr'}>{doc.altLabel} &rarr;</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
